// src/components/Layout.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { navigate } from 'gatsby';
import Sidebar from './Sidebar';
import HomeContent from './HomeContent';
import { FiActivity, FiBook, FiUser } from 'react-icons/fi';

const LayoutWrapper = styled.div`
  position: relative;
  display: flex;
  height: 100vh;
`;

const SidebarsContainer = styled(motion.div)`
  position: fixed;
  right: 0;
  display: flex;
  width: 180px;
  height: 100vh;
  z-index: 9;
  @media (max-width: 768px) {
    width: auto;
    height: auto;
    display: flex;
    flex-direction: column-reverse;
    gap: 20px;
    padding-bottom: 20px;

    /* Each circle will be positioned relative to this container */
    & > * {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      border: 2px solid black;
      margin: 0;
      flex-shrink: 0;
      position: fixed; /* Change to fixed positioning */
      right: 20px; /* Default right position */
    }

    /* Position each circle vertically */
    & > *:nth-child(1) {
      bottom: 180px;
    }
    & > *:nth-child(2) {
      bottom: 100px;
    }
    & > *:nth-child(3) {
      bottom: 20px;
    }
  }
`;

const sidebars = [
  { id: 'research', icon: <FiBook />, title: 'Research' },
  { id: 'design', icon: <FiActivity />, title: 'Design' },
  { id: 'about', icon: <FiUser />, title: 'About me' },
];

const sidebarWidth = 60;

const sidebarVariants = {
  //animation for hovering
  hovered: (custom) => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const { index, hovered, wasSelected } = custom;
    const isWasSelected = sidebars[index].id === wasSelected;

    // On mobile, return the default position without hover effects
    if (isMobile) {
      return {
        x: isWasSelected ? `calc(-100vw + ${sidebarWidth}px)` : 0,
        opacity: 1,
        cursor: 'pointer',
      };
    }

    // Desktop hover animation
    if (hovered !== null && !isWasSelected) {
      const notWasSelectedIndexes = sidebars
        .map((sidebar, i) => (sidebar.id !== wasSelected ? i : -1))
        .filter((i) => i !== -1);

      const newIndex = notWasSelectedIndexes.indexOf(index);
      const position =
        -(notWasSelectedIndexes.length - newIndex - 1) * sidebarWidth;

      if (index < hovered) {
        const distance = notWasSelectedIndexes.indexOf(hovered) - newIndex;
        return {
          x: distance * 10 + position,
          opacity: 1,
          transition: { type: 'spring', stiffness: 350, damping: 20 },
          cursor: 'pointer',
        };
      }

      return {
        x: position,
        opacity: 1,
        cursor: 'pointer',
      };
    }

    return {
      x: isWasSelected ? `calc(-100vw + ${sidebarWidth}px)` : 0,
      opacity: 1,
      cursor: 'pointer',
    };
  },

  //animation for showing up all the bars only in home page
  visible: (custom) => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    if (isMobile) {
      return {
        x: 0, // No horizontal movement needed
        opacity: 1,
        transition: {
          duration: 0.6,
          ease: 'easeOut',
          delay: custom.index * 0.3, // Shorter delay for mobile
        },
      };
    }

    // Original desktop animation
    return {
      x: (sidebars.length - custom.index - 1) * -sidebarWidth,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        delay: (sidebars.length - custom.index - 1) * 0.6,
      },
    };
  },

  //aniamtion stage 1 right after sidebar clicked
  collapse: (custom) => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const { index, active, wasSelected } = custom;
    const selectedIndex = sidebars.findIndex(
      (sidebar) => sidebar.id === active
    );
    const isSelected = index === selectedIndex;
    const isWasSelected = sidebars[index].id === wasSelected;

    if (isMobile) {
      if (isSelected) {
        return {
          x: 0,
          opacity: 1,
          transition: { duration: 0.5 },
        };
      } else {
        return {
          x: 0,
          opacity: 0,
          transition: { duration: 0.5 },
        };
      }
    }

    // Desktop animations remain unchanged
    if (isSelected) {
      return {
        x: (sidebars.length - index - 1) * -sidebarWidth,
        opacity: 1,
        transition: { duration: 0.5, ease: 'easeInOut' },
        cursor: 'none',
      };
    } else if (isWasSelected) {
      const distance = Math.abs(selectedIndex - index) * sidebarWidth;
      return {
        x: selectedIndex < index ? -distance : distance,
        opacity: 1,
        transition: { duration: 0.5, ease: 'easeInOut' },
        cursor: 'none',
      };
    } else {
      const isAfterSelected = index > selectedIndex;
      const distance = Math.abs(selectedIndex - index) * sidebarWidth;
      return {
        x: isAfterSelected ? -distance : distance,
        opacity: 0,
        transition: { duration: 0.5, ease: 'easeInOut' },
        cursor: 'none',
      };
    }
  },
  //right after collapse animation, the selected bar will move to the most right
  toRight: (custom) => ({
    x: 0,
    opacity: custom.isSelected ? 1 : 0,
    transition: { duration: 0.5, ease: 'easeInOut' },
  }),

  //the selected bar will move to most left with the page content, unselected bars remain at x:0, opacity:0
  opening: (custom) => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    if (isMobile) {
      return {
        x: custom.isSelected ? `calc(-100vw + ${1.7 * sidebarWidth}px)` : 0,
        opacity: custom.isSelected ? 1 : 0,
        transition: {
          x: { duration: 0.7, delay: 0.25 },
          ease: 'easeInOut',
        },
        cursor: custom.isSelected ? 'pointer' : 'none',
      };
    }

    return {
      x: custom.isSelected ? `calc(-100vw + ${sidebarWidth}px)` : 0,
      opacity: custom.isSelected ? 1 : 0,
      transition: { duration: 0.7, ease: 'easeInOut' },
      cursor: custom.isSelected ? 'pointer' : 'none',
    };
  },

  //keep the previously selected bar's position and opacity, show up unselected bars after a delay
  opened: (custom) => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const { index, wasSelected } = custom;
    const isWasSelected = sidebars[index].id === wasSelected;

    if (isMobile) {
      return {
        x: isWasSelected ? `calc(-100vw + ${1.7 * sidebarWidth}px)` : 0,
        opacity: 1,
        transition: {
          duration: 0.6,
          ease: 'easeInOut',
          delay: isWasSelected ? 0 : index * 0.3,
        },
        cursor: 'pointer',
      };
    }

    // Original desktop logic
    const selectedIndex = sidebars.findIndex(
      (sidebar) => sidebar.id === wasSelected
    );
    const unselectedIndexes = sidebars
      .filter((_, i) => i !== selectedIndex)
      .map((sidebar, i) => i);
    const newIndex = isWasSelected
      ? selectedIndex
      : unselectedIndexes.indexOf(index - (index > selectedIndex ? 1 : 0));
    const position = isWasSelected
      ? `calc(-100vw + ${sidebarWidth}px)`
      : -(unselectedIndexes.length - newIndex - 1) * sidebarWidth;

    return {
      x: position,
      opacity: 1,
      transition: {
        duration: isWasSelected ? 0 : 0.6,
        ease: 'easeInOut',
        delay: isWasSelected
          ? 0
          : (unselectedIndexes.length - newIndex - 1) * 0.5,
      },
      cursor: 'pointer',
    };
  },
};

const Layout = ({
  children,
  homeContent,
  activatedSidebar,
  setActivatedSidebar,
  wasSelectedSidebar,
  isPageOpen,
  prevLocation,
  animationStage,
  setAnimationStage,
  isTransitioning,
  setIsTransitioning,
  isReturning,
  setIsReturning,
}) => {
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (isTransitioning) {
      if (animationStage === 'collapse') {
        //console.log('Animation stage: collapse');
        setTimeout(() => {
          setAnimationStage('toRight');
        }, 500);
      } else if (animationStage === 'toRight') {
        //console.log('Animation stage: toRight');
        setTimeout(() => {
          setAnimationStage('opening');
        }, 900);
      } else if (animationStage === 'opening') {
        //console.log('Animation stage: opening');
        setTimeout(() => {
          navigate(`/${activatedSidebar}/`);
          //console.log('URL updated');
        }, 700);
      }
    }
    if (isReturning) {
      if (animationStage === 'toRight') {
       // console.log('Animation stage: toRight in returning');
        setTimeout(() => {
          navigate('/');
        //  console.log('URL updated');
        }, 500);
      }
    }
  }, [
    isTransitioning,
    animationStage,
    activatedSidebar,
    setAnimationStage,
    isReturning,
  ]);

  const handleSidebarHover = (index) => {
    if (
      !isTransitioning &&
      !isReturning &&
      sidebars[index].id !== wasSelectedSidebar
    ) {
      setHovered(index);
    }
  };

  const handleSidebarLeave = () => {
    setHovered(null);
  };

  const handleSidebarClick = (sidebarId) => {
    setActivatedSidebar(sidebarId);
    setIsTransitioning(true);
    setAnimationStage('collapse');
  };

  const handleReturnClick = () => {
    //do return to home action
    //console.log('return clicked');
    setIsReturning(true);
    //setWasSelectedSidebar('');
    setAnimationStage('toRight');
  };

  const getAnimationStage = (sidebar) => {
    //
    if (
      !isTransitioning &&
      !isReturning &&
      hovered !== null &&
      sidebar.id !== wasSelectedSidebar
    ) {
      return 'hovered';
    }

    if (isTransitioning || isReturning) {
      return animationStage;
    } else return isPageOpen ? 'opened' : 'visible';
  };

  return (
    <LayoutWrapper>
      <HomeContent
        homeContent={homeContent}
        globalStage={animationStage}
        activatedSidebar={activatedSidebar}
        isTransitioning={isTransitioning}
        isPageOpen={isPageOpen}
        wasSelectedSidebar={wasSelectedSidebar}
        isReturning={isReturning}
      >
        {children}
      </HomeContent>

      <SidebarsContainer>
        {sidebars.map((sidebar, index) => (
          <Sidebar
            key={sidebar.id}
            id={sidebar.id}
            icon={sidebar.icon}
            title={sidebar.title}
            isPageOpen={isPageOpen}
            onClick={() => handleSidebarClick(sidebar.id)}
            onMouseEnter={() => handleSidebarHover(index)}
            onMouseLeave={handleSidebarLeave}
            onReturnClick={() => handleReturnClick()}
            custom={{
              index,
              hovered,
              wasSelected: wasSelectedSidebar,
              active: activatedSidebar,
              isSelected: sidebar.id === activatedSidebar,
            }}
            animate={sidebarVariants[getAnimationStage(sidebar, index)]}
            zIndex={sidebar.id === activatedSidebar ? 20 : index + 10}
            isHovered={hovered === index}
          />
        ))}
      </SidebarsContainer>
    </LayoutWrapper>
  );
};

export default Layout;
